import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { Camera, CameraResultType } from "@capacitor/camera";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { v4 as uuid } from "uuid";
import { CapacitorHttp } from "@capacitor/core";
import { Elm } from "./Main.elm";
import initCustomElement from "./js/aw-image";
import Db from "./js/db";
import Http from "./js/http";
import jsonData from "../versions.json";

let PlatformVar = "";
let BaseUri = "";

async function loadBaseUri() {
    const baseUri = await Db.loadBaseUri();

    if (!baseUri) {
        Db.saveBaseUri("https://smart.awenko.de");
        BaseUri = "https://smart.awenko.de";
    } else {
        BaseUri = baseUri;
    }
}

function getLanguage() {
    return Device.getLanguageCode().then((result) => result.value.substring(0, 2).toUpperCase());
}

async function getPlatform() {
    const result = await Device.getInfo();
    PlatformVar = result.platform;

    return {
        platform: result.platform,
        version: jsonData[`${result.platform}-version`],
    };
}

function getRandomInts(n) {
    const crypto = window.crypto || window.msCrypto;
    const randInts = new Uint32Array(n);

    crypto.getRandomValues(randInts);

    return Array.from(randInts);
}

async function initFlags() {
    const language = await getLanguage();
    const platform = await getPlatform();

    const translationUrls = {
        de: "./assets/locales/de/translations.json",
        en: "./assets/locales/en/translations.json",
    };
    const network = window.navigator.onLine ? "ONLINE" : "OFFLINE";
    const randomInts = getRandomInts(5);

    return {
        seed: randomInts[0],
        seedExtension: randomInts.slice(1),
        language: language,
        translationUrls: translationUrls,
        network: network,
        baseUri: BaseUri,
        platform: platform,
        apiVersion: jsonData["api-version"],
    };
}

function mimeTypeToImageExtension(mimeType) {
    switch (mimeType) {
    case "image/jpeg":
        return ".jpg";
    case "image/png":
        return ".png";
    case "image/gif":
        return ".gif";
    default:
        return "";
    }
}

function uploadFile(apiVersion, path, id, blob) {
    const formData = new FormData();

    formData.append("file", blob, id);

    const url = `${BaseUri}/files/upload/${id}`;

    const reader = new FileReader();
    reader.onload = () => {
        const options = {
            url: url,
            headers: {
                "x-api-version": apiVersion,
                "x-path": path,
                "Content-Type": "application/json",
            },
            webFetchExtra: {
                credentials: "include",
            },
            data: {
                content: reader.result,
            },
            name: "file",
        };

        return CapacitorHttp.post(options);
    };
    reader.readAsDataURL(blob, "ISO-8859-1");
}

function downloadFile(apiVersion, path, id) {
    const url = `${BaseUri}/files/download/${id}`;

    const options = {
        method: "GET",
        url: url,
        headers: {
            "x-api-version": apiVersion,
            "x-path": path,
        },
        webFetchExtra: {
            credentials: "include",
        },
        responseType: "blob",
    };

    return CapacitorHttp.get(options)
        .then(async (response) => {
            if (response.status !== 200) {
                return { status: response.status, blob: null };
            }

            const byteChars = atob(response.data);
            const byteNumbers = Array.prototype.map.call(byteChars, (byte) => byte.charCodeAt(0));
            const byteArray = new Uint8Array(byteNumbers);

            return { status: response.status, blob: new Blob([byteArray], { type: response.headers["content-type"] }) };
        });
}

function deleteFile(apiVersion, path, id) {
    const url = `${BaseUri}/files/delete/${id}`;

    const options = {
        method: "POST",
        url: url,
        headers: {
            "x-api-version": apiVersion,
            "x-path": path,
        },
        webFetchExtra: {
            credentials: "include",
        },
    };

    return CapacitorHttp.post(options)
        .then((response) => response.status);
}

function initEnvironment(outbox, inbox) {
    App.addListener("appStateChange", (state) => {
        inbox.send({
            type: "CHANGED_APP_STATE",
            state: state.isActive ? "ACTIVE" : "INACTIVE",
        });
    });

    window.addEventListener("online", () => {
        inbox.send({ type: "CHANGED_NETWORK_STATE", state: "ONLINE" });
    }, false);

    window.addEventListener("offline", () => {
        inbox.send({ type: "CHANGED_NETWORK_STATE", state: "OFFLINE" });
    });

    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_SESSION": {
            let session = await Db.loadSession();
            Http
                .sessionCheck(`${BaseUri}/session-check`)
                .then(async (response) => {
                    if (response.status !== 200) {
                        if (!session) {
                            await Db.deleteSession();
                            session = undefined;
                        } else {
                            inbox.send({
                                type: "SESSION_EXPIRED",
                            });
                        }
                    }
                });
            inbox.send({
                type: "RECEIVED_SESSION",
                session: session,
            });
            // ToDo csrf token send per Header instead of cookie

            break;
        }
        case "SAVE_SESSION": {
            await Db.saveSession(msg.session);

            inbox.send({ type: "SAVED_SESSION", session: msg.session });
            break;
        }
        case "DELETE_SESSION": {
            await Db.deleteSession();
            break;
        }
        case "REMOVE_DATA": {
            await Db.removeData();
            inbox.send({ type: "REMOVED_DATA", session: msg.session });
            break;
        }
        case "LOAD_USER_RIGHTS": {
            let userRights = await Db.loadUserRights();

            if (!userRights) {
                userRights = { state: "NOTINITIALIZED" };
            }

            inbox.send({ type: "LOADED_USER_RIGHTS", userRights: userRights });
            break;
        }
        case "SAVE_USER_RIGHTS": {
            await Db.saveUserRights(msg.userRights);

            inbox.send({ type: "SAVED_USER_RIGHTS", userRights: msg.userRights });
            break;
        }
        default:
            break;
        }
    });
}

function loginCase(msg, inbox) {
    const data = {
        tenantName: msg.tenantName,
        username: msg.username,
        password: msg.password,
    };
    Http
        .login(`${BaseUri}/login`, data)
        .then((response) => {
            if (response.status === 200) {
                inbox.send({
                    type: "LOGIN_SUCCEEDED",
                    tenantId: response.data.tenantId,
                    userId: response.data.userId,
                });
            } else if (response.status === 401) {
                inbox.send({
                    type: "LOGIN_FAILED",
                    msg: "LOGIN_INVALID",
                });
            } else {
                inbox.send({
                    type: "LOGIN_FAILED",
                    msg: "SERVER_ERROR",
                });
            }
        })
        .catch(() => {
            inbox.send({
                type: "LOGIN_FAILED",
                msg: "TIMEOUT",
            });
        });
}

function initLogin(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOGIN": {
            loginCase(msg, inbox);
            break;
        }
        case "GET_CURRENT_URL": {
            inbox.send({ type: "CURRENT_URL", url: window.location.href });
            break;
        }
        case "SAVE_BASE_URI": {
            await Db.saveBaseUri(msg.baseUri);
            BaseUri = msg.baseUri;
            await Http.sessionCheck(`${BaseUri}/session-check`);
            inbox.send({ type: "SAVED_BASE_URI" });
            break;
        }
        default:
            break;
        }
    });
}

function initRelogin(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOGIN": {
            loginCase(msg, inbox);
            break;
        }
        case "LOAD_TENANT": {
            const tenant = await Db.loadTenantData();
            inbox.send({
                type: "GOT_TENANT",
                tenant: tenant.tenant,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initEventStream(outbox, inbox) {
    let eventStream;

    async function createUri(parameters) {
        const searchParameters = new URLSearchParams();

        if (parameters && parameters.userEventId) {
            searchParameters.append("userEventId", parameters.userEventId);
        }

        if (parameters && parameters.organisationUnitEventId) {
            searchParameters.append("organisationUnitEventId", parameters.organisationUnitEventId);
        }

        if (parameters && parameters.planEventId) {
            searchParameters.append("planEventId", parameters.planEventId);
        }

        if (parameters && parameters.measureEventId) {
            searchParameters.append("measureEventId", parameters.measureEventId);
        }

        if (parameters && parameters.auditEventId) {
            searchParameters.append("auditEventId", parameters.auditEventId);
        }

        if (parameters && parameters.tenantEventId) {
            searchParameters.append("tenantEventId", parameters.tenantEventId);
        }

        if (parameters && parameters.checkpointDataEventId) {
            searchParameters.append("checkpointGroupEventId", parameters.checkpointDataEventId);
        }

        if (parameters && parameters.rightEventId) {
            searchParameters.append("rightEventId", parameters.rightEventId);
        }

        if (PlatformVar === "ios") {
            const response = await Http.get(`${BaseUri}/generate-token`, []);

            const json = response.status === 200 ? JSON.parse(response.data) : undefined;

            if (!!json && !!json.token) {
                searchParameters.append("token", JSON.parse(response.data).token);
            }
        }
        searchParameters.append("apiVersion", jsonData["api-version"]);

        return `${BaseUri}/sync/?${searchParameters.toString()}`;
    }

    async function connect(parameters) {
        eventStream = new EventSource(await createUri(parameters), { withCredentials: true });

        eventStream.onopen = () => {
            inbox.send({ type: "STATUS", state: { status: "CONNECTED" } });
        };

        eventStream.onerror = (error) => {
            if (eventStream) {
                eventStream.close();
                eventStream = null;

                inbox.send({ type: "STATUS", state: { status: "ERROR", error: JSON.stringify(error) } });
            }
        };

        eventStream.addEventListener("user-status", (event) => {
            inbox.send({ type: "USER_STATUS", status: event.data });
        });

        eventStream.addEventListener("user-event", (event) => {
            inbox.send({ type: "USER_EVENT", event: JSON.parse(event.data) });
        });

        eventStream.addEventListener("organisation-unit-status", (event) => {
            inbox.send({ type: "ORGANISATION_UNIT_STATUS", status: event.data });
        });

        eventStream.addEventListener("organisation-unit-event", (event) => {
            inbox.send({ type: "ORGANISATION_UNIT_EVENT", event: JSON.parse(event.data) });
        });

        eventStream.addEventListener("plan-status", (event) => {
            inbox.send({ type: "PLAN_STATUS", status: event.data });
        });

        eventStream.addEventListener("plan-event", (event) => {
            inbox.send({ type: "PLAN_EVENT", event: JSON.parse(event.data) });
        });

        eventStream.addEventListener("audit-status", (event) => {
            inbox.send({ type: "AUDIT_STATUS", status: event.data });
        });

        eventStream.addEventListener("audit-event", (event) => {
            inbox.send({ type: "AUDIT_EVENT", event: JSON.parse(event.data) });
        });

        eventStream.addEventListener("measure-status", (event) => {
            inbox.send({ type: "MEASURE_STATUS", status: event.data });
        });

        eventStream.addEventListener("measure-event", (event) => {
            inbox.send({ type: "MEASURE_EVENT", event: JSON.parse(event.data) });
        });

        eventStream.addEventListener("tenant-status", (event) => {
            inbox.send({ type: "TENANT_STATUS", status: event.data });
        });

        eventStream.addEventListener("tenant-event", (event) => {
            inbox.send({ type: "TENANT_EVENT", event: JSON.parse(event.data) });
        });

        eventStream.addEventListener("checkpoint-group-status", (event) => {
            inbox.send({ type: "CHECKPOINT_DATA_STATUS", status: event.data });
        });

        eventStream.addEventListener("checkpoint-group-event", (event) => {
            inbox.send({ type: "CHECKPOINT_DATA_EVENT", event: JSON.parse(event.data) });
        });

        eventStream.addEventListener("right-status", (event) => {
            inbox.send({ type: "RIGHT_STATUS", status: event.data });
        });

        eventStream.addEventListener("right-event", (event) => {
            inbox.send({ type: "RIGHTS_EVENT", event: JSON.parse(event.data) });
        });
    }

    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "CONNECT": {
            await connect(msg.parameters);
            break;
        }
        case "DISCONNECT": {
            if (eventStream) {
                eventStream.close();
                eventStream = null;
            }
            inbox.send({ type: "STATUS", state: { status: "DISCONNECTED" } });
            break;
        }
        default:
            break;
        }
    });
}

function initFileStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_FILES": {
            const files = await Db.loadFiles();

            inbox.send({ type: "LOADED_FILES", files: files });
            break;
        }
        case "SAVE_FILES": {
            await Db.saveFiles(msg.files);

            inbox.send({ type: "SAVED_FILES" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveFileModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "UPLOAD_FILE": {
            const { file } = msg;
            const { id } = file.filename;

            Db.loadBlob(id)
                .then((blob) => uploadFile(msg.apiVersion, msg.path, id, blob))
                .then(() => {
                    file.status = "SYNCHRONIZED";

                    return Db.saveFile(file);
                })
                .then(() => inbox.send({ type: "UPLOADED_FILE", file: msg.file }));
            break;
        }
        case "DOWNLOAD_FILE": {
            const { file } = msg;
            const { id } = file.filename;

            downloadFile(msg.apiVersion, msg.path, id)
                // eslint-disable-next-line consistent-return
                .then((response) => {
                    if (response.status !== 200) {
                        inbox.send({ type: "ERROR_DOWNLOADING_FILE", file: msg.file, status: response.status });
                    } else {
                        file.status = "SYNCHRONIZED";
                        return Db.saveFileAndBlob(file, response.blob);
                    }
                })
                .then(() => {
                    inbox.send({ type: "DOWNLOADED_FILE", file: msg.file });
                });
            break;
        }
        case "DELETE_FILE": {
            const { file } = msg;
            const { id } = file.filename;

            deleteFile(msg.apiVersion, msg.path, id)
                .then(() => {
                    Db.deleteFile(id)
                        .then(() => inbox.send({ type: "DELETED_FILE", file: msg.file.filename }));
                });
            break;
        }
        default:
            break;
        }
    });
}

function initTmpFileStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "DOWNLOAD_TMP_RECORD_FILE": {
            const { id } = msg.filename;

            downloadFile(msg.apiVersion, msg.path, id)
                .then((response) => {
                    if (response.status !== 200) {
                        inbox.send({ type: "ERROR_DOWNLOADING_RECORD_FILE", status: response.status, recordId: msg.recordId });
                    } else {
                        const blobUrl = URL.createObjectURL(response.blob);

                        inbox.send({ type: "DOWNLOADED_TMP_RECORD_FILE", blobUrl: blobUrl, recordId: msg.recordId });
                    }
                });
            break;
        }
        case "DOWNLOAD_TMP_MEASURE_FILE": {
            const { id } = msg.filename;

            downloadFile(msg.apiVersion, msg.path, id)
                .then((response) => {
                    if (response.status !== 200) {
                        inbox.send({ type: "ERROR_DOWNLOADING_MEASURE_FILE", status: response.status, filename: msg.filename });
                        throw new Error("DOWNLOAD_FILE ERROR");
                    } else {
                        const blobUrl = URL.createObjectURL(response.blob);

                        inbox.send({ type: "DOWNLOADED_TMP_MEASURE_FILE", blobUrl: blobUrl, filename: msg.filename });
                    }
                });
            break;
        }
        default:
            break;
        }
    });
}

function initUserStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const userData = await Db.loadUserData();

            inbox.send({ type: "LOADED_DATA", data: userData });
            break;
        }
        case "SAVE_USERS": {
            await Db.saveUsers(msg.users);

            inbox.send({ type: "SAVED_USERS" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveUserModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.saveUserCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removeUserCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removeUserCommandAndEvent(msg.commandId, msg.userId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                userId: msg.userId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initHttpPort(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "SEND_POST": {
            Http
                .post(msg.url, msg.data, msg.headerArray)
                .then((response) => {
                    let data;
                    try {
                        data = JSON.parse(response.data);
                    } catch (error) {
                        data = response.data;
                    }

                    inbox.send({
                        type: "GOT_RESPONSE",
                        resultType: msg.resultType,
                        command: msg.data,
                        status: response.status,
                        data: data,
                    });
                })
                .catch(() => {
                    inbox.send({
                        type: "GOT_ERROR_RESPONSE",
                        resultType: msg.resultType,
                    });
                });
            break;
        }
        case "SEND_GET": {
            Http
                .get(msg.url, msg.headerArray)
                .then((response) => {
                    let data;
                    try {
                        data = JSON.parse(response.data);
                    } catch (error) {
                        data = response.data;
                    }

                    inbox.send({
                        type: "GOT_RESPONSE",
                        resultType: msg.resultType,
                        status: response.status,
                        data: data,
                    });
                })
                .catch(() => {
                    inbox.send({
                        type: "GOT_ERROR_RESPONSE",
                        resultType: msg.resultType,
                    });
                });
            break;
        }
        default:
            break;
        }
    });
}

function initOrganisationUnitStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const organisationUnitData = await Db.loadOrganisationUnitData();

            inbox.send({ type: "LOADED_DATA", data: organisationUnitData });
            break;
        }
        case "SAVE_ORGANISATION_UNITS": {
            await Db.saveOrganisationUnits(msg.organisationUnits);

            inbox.send({ type: "SAVED_ORGANISATION_UNITS" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveOrganisationUnitModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.saveOrganisationUnitCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removeOrganisationUnitCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removeOrganisationUnitCommandAndEvent(msg.commandId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initPlanStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const planData = await Db.loadPlanData();

            inbox.send({ type: "LOADED_DATA", data: planData });
            break;
        }
        case "SAVE_PLANS": {
            await Db.savePlans(msg.plans);

            inbox.send({ type: "SAVED_PLANS" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.savePlanModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.savePlanCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removePlanCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removePlanCommandAndEvent(msg.commandId, msg.planId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                planId: msg.planId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initAuditStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const auditData = await Db.loadAuditData();

            inbox.send({ type: "LOADED_DATA", data: auditData });
            break;
        }
        case "SAVE_AUDITS": {
            await Db.saveAudits(msg.audits);

            inbox.send({ type: "SAVED_AUDITS" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveAuditModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.saveAuditCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removeAuditCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removeAuditCommandAndEvent(msg.commandId, msg.auditId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                auditId: msg.auditId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initMeasureStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const measureData = await Db.loadMeasureData();

            inbox.send({ type: "LOADED_DATA", data: measureData });
            break;
        }
        case "SAVE_MEASURES": {
            await Db.saveMeasures(msg.measures);

            inbox.send({ type: "SAVED_MEASURES" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveMeasureModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.saveMeasureCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removeMeasureCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removeMeasureCommandAndEvent(msg.commandId, msg.measureId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                measureId: msg.measureId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initTenantStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const tenantData = await Db.loadTenantData();

            inbox.send({ type: "LOADED_DATA", data: tenantData });
            break;
        }
        case "SAVE_TENANT": {
            await Db.saveTenant(msg.tenant);

            inbox.send({ type: "SAVED_TENANT" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveTenantModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.saveTenantCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removeTenantCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removeTenantCommandAndEvent(msg.commandId, msg.tenantId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                tenantId: msg.tenantId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initCheckpointDataStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const checkpointData = await Db.loadCheckpointData();

            inbox.send({ type: "LOADED_DATA", data: checkpointData });
            break;
        }
        case "SAVE_CHECKPOINT_DATA": {
            await Db.saveCheckpointData(msg.checkpointData);

            inbox.send({ type: "SAVED_CHECKPOINT_DATA" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveCheckpointDataModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.saveCheckpointDataCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removeCheckpointDataCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removeCheckpointDataCommandAndEvent(msg.commandId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function initRightsStore(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "LOAD_DATA": {
            const rights = await Db.loadRights();

            inbox.send({ type: "LOADED_DATA", data: rights });
            break;
        }
        case "SAVE_RIGHTS": {
            await Db.saveRights(msg.rights);

            inbox.send({ type: "SAVED_RIGHTS" });
            break;
        }
        case "SAVE_MODIFICATIONS": {
            await Db.saveRightsModifications(msg.modifications);

            inbox.send({ type: "SAVED_MODIFICATIONS" });
            break;
        }
        case "SAVE_COMMANDS_AND_EVENTS": {
            await Db.saveRightsCommandsAndEvents(msg.commands, msg.events);

            inbox.send({ type: "SAVED_COMMANDS_AND_EVENTS" });
            break;
        }
        case "REMOVE_COMMAND": {
            await Db.removeRightsCommand(msg.commandId);

            inbox.send({ type: "REMOVED_COMMAND" });
            break;
        }
        case "REMOVE_COMMAND_AND_EVENT": {
            await Db.removeRightsCommandAndEvent(msg.commandId, msg.eventVersion);

            inbox.send({
                type: "REMOVED_COMMAND_AND_EVENT",
                commandId: msg.commandId,
                eventVersion: msg.eventVersion,
            });
            break;
        }
        default:
            break;
        }
    });
}

function takePhoto() {
    return Camera
        .getPhoto({
            quality: 100,
            allowEditing: false,
            saveToGallery: false,
            resultType: CameraResultType.Uri,
        })
        .then((cameraPhoto) => fetch(cameraPhoto.webPath))
        .then((response) => response.blob());
}

function selectImages() {
    return new Promise((resolve) => {
        const input = document.createElement("input");

        input.type = "file";
        input.multiple = true;
        input.accept = "image/*";
        input.onchange = (event) => {
            const acc = [];
            const cnt = event.target.files.length;
            for (let i = 0; i < event.target.files.length; i += 1) {
                const file = event.target.files[i];
                const reader = new FileReader();
                const { name } = file;

                reader.onload = async (readerEvent) => {
                    const arrayBuffer = readerEvent.target.result;
                    const blob = new Blob([arrayBuffer], { type: file.type });

                    const result = {
                        blob: blob,
                        name: name,
                    };

                    acc.push(result);

                    if (cnt === acc.length) {
                        resolve(acc);
                    }
                };

                reader.readAsArrayBuffer(file);
            }
        };

        input.click();
    });
}

function selectImage() {
    return new Promise((resolve) => {
        const input = document.createElement("input");

        input.type = "file";
        input.accept = "image/*";
        input.onchange = (event) => {
            const file = event.target.files[0];
            const reader = new FileReader();
            const { name } = event.target.files[0];

            reader.onload = async (readerEvent) => {
                const arrayBuffer = readerEvent.target.result;
                const blob = new Blob([arrayBuffer], { type: file.type });

                const result = {
                    blob: blob,
                    name: name,
                };

                resolve(result);
            };

            reader.readAsArrayBuffer(file);
        };

        input.click();
    });
}

function initFileHandler(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "TAKE_PHOTO": {
            const blob = await takePhoto();
            const id = uuid().toString();

            await Db.saveBlob(id, blob);

            inbox.send({ type: "PHOTO_TAKEN", filename: { id: id, name: `${id}${mimeTypeToImageExtension(blob.type)}` } });
            break;
        }
        case "SELECT_IMAGE": {
            const selectImageResult = await selectImage();
            const { blob } = selectImageResult;
            const fileExtension = mimeTypeToImageExtension(blob.type);

            if (fileExtension === "") {
                inbox.send({ type: "INVALID_FILE_TYPE" });
                break;
            }

            const fileId = `${uuid().toString()}`;
            const { name } = selectImageResult;

            await Db.saveBlob(fileId, blob);

            inbox.send({ type: "IMAGE_SELECTED", filename: { id: fileId, name: name } });
            break;
        }
        case "SELECT_IMAGES": {
            const selectImagesResult = await selectImages();
            for (let i = 0; i < selectImagesResult.length; i += 1) {
                const { blob } = selectImagesResult[i];
                const fileExtension = mimeTypeToImageExtension(blob.type);

                if (fileExtension === "") {
                    inbox.send({ type: "INVALID_FILE_TYPE" });
                    break;
                }

                const fileId = `${uuid().toString()}`;
                const { name } = selectImagesResult[i];

                Db.saveBlob(fileId, blob).then(() => {
                    inbox.send({ type: "IMAGE_SELECTED", filename: { id: fileId, name: name } });
                });
            }

            break;
        }
        default:
            break;
        }
    });
}

function copyToClipboard(outbox, inbox) {
    function copy(value) {
        const dummy = document.createElement("textarea");

        document.body.appendChild(dummy);

        dummy.value = value;

        if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
            dummy.contentEditable = true;

            dummy.readOnly = true;

            const range = document.createRange();

            range.selectNodeContents(dummy);

            const selection = window.getSelection();

            selection.removeAllRanges();

            selection.addRange(range);

            selection.setSelectionRange(0, 999999);
        } else {
            dummy.select();
        }

        document.execCommand("copy");

        document.body.removeChild(dummy);
    }

    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "COPY_TEXT": {
            copy(msg.text);

            inbox.send({ type: "COPIED" });
            break;
        }
        case "COPY_JSON": {
            copy(JSON.stringify(msg.json));

            inbox.send({ type: "COPIED" });
            break;
        }
        default:
            break;
        }
    });
}

function initCanvas(outbox, inbox) {
    outbox.subscribe(async (msg) => {
        switch (msg.type) {
        case "SAVE": {
            const canvas = document.getElementsByTagName("canvas")[0];
            canvas.toBlob(async (blob) => {
                const id = uuid();
                const name = `${id.toString()}${mimeTypeToImageExtension(blob.type)}`;

                await Db.saveBlob(id, blob);

                inbox.send({ type: "BLOB_SAVED", filename: { id: id, name: name } });
            });
            break;
        }
        default:
            break;
        }
    });
}

function initPorts(ports) {
    initEnvironment(ports.environmentOutbox, ports.environmentInbox);
    initLogin(ports.loginOutbox, ports.loginInbox);
    initRelogin(ports.reLoginOutbox, ports.reLoginInbox);
    initEventStream(ports.eventStreamOutbox, ports.eventStreamInbox);
    initFileStore(ports.fileStoreOutbox, ports.fileStoreInbox);
    initTmpFileStore(ports.tmpFileStoreOutbox, ports.tmpFileStoreInbox);
    initUserStore(ports.userStoreOutbox, ports.userStoreInbox);
    initHttpPort(ports.httpOutbox, ports.httpInbox);
    initOrganisationUnitStore(ports.organisationUnitStoreOutbox, ports.organisationUnitStoreInbox);
    initPlanStore(ports.planStoreOutbox, ports.planStoreInbox);
    initAuditStore(ports.auditStoreOutbox, ports.auditStoreInbox);
    initMeasureStore(ports.measureStoreOutbox, ports.measureStoreInbox);
    initFileHandler(ports.measureImageEditorOutbox, ports.measureImageEditorInbox);
    initFileHandler(ports.imageEditorOutbox, ports.imageEditorInbox);
    initTenantStore(ports.tenantStoreOutbox, ports.tenantStoreInbox);
    initCheckpointDataStore(ports.checkpointDataStoreOutbox, ports.checkpointDataStoreInbox);
    initRightsStore(ports.rightsStoreOutbox, ports.rightsStoreInbox);
    copyToClipboard(ports.copyToClipboardOutbox, ports.copyToClipboardInbox);
    initCanvas(ports.canvasOutbox, ports.canvasInbox);
}

defineCustomElements(window)
    .then(async () => {
        await loadBaseUri();

        initCustomElement();

        const app = Elm.Main.init({
            node: document.getElementById("main"),
            flags: await initFlags(),
        });

        initPorts(app.ports);
    });
